// 批次 23（#25，Gitea issue #25）：表單設計（form-template-form-template-list，origin
// formTemplate/formTemplateList.jsp 418 行；display 頁型 → complexBody 整頁置換，
// DisplayPage:49-50 消費）。查詢表單 9 欄（含 hidden formTemplateId/csrfToken）＋表 8 欄
// （刪除鈕/職類/科部/表單名稱 showEdit/用途映射/啟用/審核四態＋頁型鈕/排序，:39-124）＋
// doQuery（:213-249：getFormTemplateDetailListAjax、order col7 asc）＋doDelete（:252-268：
// sysConfirm『是否確定要刪除?』→deleteFormTemplateAjax→doQuery＋showInfoDialog(desc)）＋
// sendVerify（:269-281：verifyFormTemplateAjax id/target='FormTemplate'/verify→doQuery＋info）＋
// 新增/列點擊 showEdit → updateFormTemplate（:201-212：form submit → formTemplate.jsp 表單精靈）。
//
// 誠實標註（D23-x）：
//   - 刪除鈕權限條件（isPermitDelete＋跨部門規則，:41-51）簡化為全列顯示（React 無權限模型，
//     D20-2 同因；admin 捕捉 isPermitDelete=true）。
//   - 審核鈕的 verifyerId==loginAccountId（:107-109）→ 種子 verifyerIsMe 旗標等價
//     （B21-2 種子旗標先例；React Session 無帳號 id 欄位）。
//   - rememberQueryCondition cookie 記憶查詢條件（:282-296）＋firstGoPage 回頁記憶（:126-130/:220-227）
//     不鏡像（mock 列表無分頁記憶；D23-6）。
//   - 新增/修改 → 表單精靈（updateFormTemplate form.submit 同窗導航）→ 站內導航
//     /formTemplate/formTemplateList/formTemplate（帶 ?formTemplateId= 參照 URL 形狀；
//     目標頁不消費＝D20-7 同款）。
//   - 審核/刪除成功 info＝resp.desc（server 回應）→「操作成功【雛形值待實機校準】」。
//   - 查詢 filters 直送（verifyStatus ALL→''、enableStatus -1→'' 哨兵轉換；C5-h）。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { ContentPanel, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_JOB_TYPE_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
} from '@/mocks/resource-rules';
import { legacyAlertFallback } from '../templates/page-behaviors';
import { CommonFormTemplateAddModal } from './CommonFormTemplateAddModal';

/** 表單類型選項（formTypeList 下拉，legacy 由 server 帶）【雛形值待實機校準】。 */
export const FORM_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '0', label: '全部' },
  { value: '1', label: '檢核表' },
  { value: '2', label: '問卷' },
  { value: '3', label: '評估表' },
];

/** 用途選項（formTemplateList.jsp:329-339）。 */
const FORM_TEMPLATE_PURPOSE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'plan', label: '訓練計畫' },
  { value: 'class', label: '課程' },
  { value: 'studyItem', label: '學習項目' },
  { value: 'co-editor-form', label: '共同編輯' },
];

/** 審核狀態選項（:340-349：ALL=全部/空=未審核/NOT=審核中/PASS=通過/FAIL=未通過）。 */
const FORM_VERIFY_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'ALL', label: '全部' },
  { value: '', label: '未審核' },
  { value: 'NOT', label: '審核中' },
  { value: 'PASS', label: '通過' },
  { value: 'FAIL', label: '未通過' },
];

/** 啟用狀態選項（:351-358：-1=全部＋enableStatusList）。 */
const FORM_ENABLE_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '-1', label: '全部' },
  { value: '1', label: '啟用' },
  { value: '0', label: '停用' },
];

/** 用途欄映射（formTemplateList.jsp:60-73）。 */
function purposeDisplayOf(row: Row): string {
  switch (String(row['purpose'] ?? '')) {
    case 'plan':
      return '訓練計畫';
    case 'class':
      return '課程';
    case 'general':
      return '一般表單';
    case 'studyItem':
      return '學習項目';
    case 'co-editor-form':
      return '共同編輯';
    default:
      return 'N/A';
  }
}

/** 審核狀態字（:97-106）：空=未審核/NOT=審核中/PASS=通過/FAIL=未通過。 */
function verifyDisplayOf(row: Row): string {
  const verify = String(row['verify'] ?? '');
  if (verify === '') return '未審核';
  if (verify === 'NOT') return '審核中';
  if (verify === 'PASS') return '通過';
  return '未通過';
}

/** 表單精靈導航（updateFormTemplate，:201-212：form submit→formTemplate.jsp）。 */
const FORM_TEMPLATE_PAGE_PATH = '/formTemplate/formTemplateList/formTemplate';

export function FormTemplateListBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const navigate = useNavigate();

  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數
  // （規格 §7.1；照已接線 7 檔同構一行式；override 命中含空陣列即移除 placeholder 空選項）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  // 查詢表單（required 3 欄由 server＋JS 預選帳號值→池首【雛形值待實機校準】）。
  const [organizationId, setOrganizationId] = useState(ACCOUNT_ORGANIZATION_OPTIONS[0]!.value);
  const [jobTypeId, setJobTypeId] = useState(ACCOUNT_JOB_TYPE_OPTIONS[0]!.value);
  const [departmentId, setDepartmentId] = useState(ACCOUNT_DEPARTMENT_OPTIONS[0]!.value);
  const [formTypeId, setFormTypeId] = useState('0');
  const [purpose, setPurpose] = useState('');
  const [verifyStatus, setVerifyStatus] = useState('ALL');
  const [enableStatus, setEnableStatus] = useState('-1');
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);

  async function doQuery(): Promise<void> {
    setBusy(true);
    try {
      // formTemplate/getFormTemplateDetailListAjax（:229）。哨兵：ALL→''、-1→''（matchesFilters 空值略過）。
      const result = await dataSource.list(pageId, {
        page: 1,
        pageSize: 50,
        filters: {
          organizationId,
          jobTypeId,
          departmentId,
          formTypeId: formTypeId === '0' ? '' : formTypeId,
          purpose,
          verifyStatus: verifyStatus === 'ALL' ? '' : verifyStatus,
          enableStatus: enableStatus === '-1' ? '' : enableStatus,
          formTemplateId: '0',
          csrfToken: '',
        },
      });
      setRows(result.rows);
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 新增／修改（updateFormTemplate :201-212：formTemplateId 帶 0=id）。 */
  function updateFormTemplate(id: string): void {
    navigate(`${FORM_TEMPLATE_PAGE_PATH}?formTemplateId=${encodeURIComponent(id)}`);
  }

  /** 刪除（doDelete :252-268）。 */
  async function doDelete(row: Row): Promise<void> {
    const confirmed = await dialogs.confirm('是否確定要刪除?');
    if (!confirmed) {
      return;
    }
    setBusy(true);
    try {
      // formTemplate/deleteFormTemplateAjax（:256；C5-j：真呼叫 DataSource 才標 data-effect）。
      await dataSource.remove(pageId, String(row['id'] ?? ''));
      await doQuery();
      await dialogs.info('刪除成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 審核（sendVerify :269-281：verifyFormTemplateAjax）。 */
  async function sendVerify(row: Row, verify: string): Promise<void> {
    setBusy(true);
    try {
      // C5-j：真呼叫 DataSource（update op 誠實標註；endpoint 未映射＝批次 21 D21-4 同款）。
      await dataSource.update(pageId, String(row['id'] ?? ''), { verify });
      await doQuery();
      await dialogs.info('審核完成【雛形值待實機校準】');
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  const columns: readonly TableColumn[] = [
    { key: 'delete', header: '', align: 'center' },
    { key: 'jobTypeName', header: '職類', align: 'center' },
    { key: 'departmentName', header: '科部', align: 'center' },
    { key: 'name', header: '表單名稱', align: 'center' },
    { key: 'purpose', header: '表單用途', align: 'center' },
    { key: 'enable', header: '啟用狀態', align: 'center' },
    { key: 'verify', header: '審核狀態', align: 'center' },
    { key: 'sort', header: '排序序號', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      {/* hidden formTemplateId/csrfToken（formTemplateList.jsp formTemplateForm；雛形值空/0）。 */}
      <input type="hidden" name="formTemplateId" value="0" readOnly />
      <input type="hidden" name="csrfToken" value="" readOnly />
      <ContentPanel uiId={`${pageId}-panel`} title="表單設計" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢表單（formTemplateList.jsp:304-389；required 3 欄池首預選＝D23-4）。 */}
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          {(
            [
              ['organizationId', '醫院：', overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS, organizationId, setOrganizationId],
              ['jobTypeId', '職類：', overrideOptions('jobTypeId') ?? ACCOUNT_JOB_TYPE_OPTIONS, jobTypeId, setJobTypeId],
              ['departmentId', '科部：', overrideOptions('departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS, departmentId, setDepartmentId],
              ['formTypeId', '表單類型：', overrideOptions('formTypeId') ?? FORM_TYPE_OPTIONS, formTypeId, setFormTypeId],
              ['purpose', '用途：', overrideOptions('purpose') ?? FORM_TEMPLATE_PURPOSE_OPTIONS, purpose, setPurpose],
              ['verifyStatus', '審核狀態：', overrideOptions('verifyStatus') ?? FORM_VERIFY_STATUS_OPTIONS, verifyStatus, setVerifyStatus],
              ['enableStatus', '啟用狀態：', overrideOptions('enableStatus') ?? FORM_ENABLE_STATUS_OPTIONS, enableStatus, setEnableStatus],
            ] as const
          ).map(([keyName, label, options, value, setValue]) => (
            <div key={keyName} className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-query-${keyName}`}>
                {keyName === 'organizationId' || keyName === 'jobTypeId' || keyName === 'departmentId' ? (
                  <span className="text-danger">*</span>
                ) : null}
                {label}
              </label>
              <Select
                uiId={`${pageId}-query-${keyName}`}
                name={keyName}
                value={value}
                onChange={(next) => setValue(next)}
                options={options}
                {...(overrideOptions(keyName) ? {} : { placeholder: '請選擇' })}
              />
            </div>
          ))}
          <div className="col-span-2 flex gap-2">
            {/* 新增（:305 updateFormTemplate(0)）→ 表單精靈。 */}
            <Button uiId={`${pageId}-add-btn`} variant="primary" size="sm" type="button" onClick={() => updateFormTemplate('0')}>
              新增
            </Button>
            <Button uiId={`${pageId}-query-btn`} variant="default" size="sm" type="button" disabled={busy} onClick={() => void doQuery()}>
              查詢
            </Button>
          </div>
        </div>
        {/* 結果表（欄定義 formTemplateList.jsp:39-124；8 欄）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows}
          rowKey={(row) => String(row['id'] ?? '').toLowerCase()}
          emptyText={t('table.empty')}
          renderCell={(row, columnKey) => {
            const id = String(row['id'] ?? '').toLowerCase();
            const verify = String(row['verify'] ?? '');
            if (columnKey === 'delete') {
              // 刪除鈕（:41-51；權限條件簡化＝D23-5）。
              return (
                <Button
                  uiId={`${pageId}-result-row-${id}-delete`}
                  variant="danger"
                  size="sm"
                  type="button"
                  disabled={busy}
                  // C5-j：刪除真呼叫 dataSource.remove（doDelete :252-268）。
                  dataEffect={`remove:${pageId}`}
                  onClick={() => {
                    void doDelete(row);
                  }}
                >
                  刪除
                </Button>
              );
            }
            if (columnKey === 'name') {
              // showEdit（:126-131 → updateFormTemplate(row.id) → 表單精靈）。
              return (
                <button
                  type="button"
                  className="cursor-pointer text-[#1ab394] underline"
                  data-ui-id={`${pageId}-result-row-${id}-edit`}
                  onClick={() => updateFormTemplate(String(row['id'] ?? ''))}
                >
                  {String(row['name'] ?? '')}
                </button>
              );
            }
            if (columnKey === 'purpose') {
              return purposeDisplayOf(row);
            }
            if (columnKey === 'enable') {
              // 啟用/停用（:87-96：text-primary 啟用／text-danger 停用）。
              return String(row['enable'] ?? '') === '1' ? (
                <span data-ui-id={`${pageId}-result-row-${id}-enable`} className="text-[#1ab394]">啟用</span>
              ) : (
                <span data-ui-id={`${pageId}-result-row-${id}-enable`} className="text-danger">停用</span>
              );
            }
            if (columnKey === 'verify') {
              const canSend = verify === '' || verify === 'FAIL';
              const canPass = verify === 'NOT' && String(row['verifyerIsMe'] ?? '') === '1';
              const hasNew = Number(row['unverifyVersionCount'] ?? 0) > 0;
              // 審核四態字色（:100-105：未審核=text-success／審核中+未通過=text-danger）。
              return (
                <span data-ui-id={`${pageId}-result-row-${id}-verify`}>
                  <span className={verifyDisplayOf(row) === '未審核' ? 'text-[#1ab394]' : verifyDisplayOf(row) === '審核中' || verifyDisplayOf(row) === '未通過' ? 'text-danger' : ''}>
                    {verifyDisplayOf(row)}
                  </span>
                  {canSend ? (
                    <Button
                      uiId={`${pageId}-result-row-${id}-send-verify`}
                      variant="default"
                      size="sm"
                      type="button"
                      disabled={busy}
                      // C5-j：審核真呼叫 dataSource.update（sendVerify :269-281；update op 誠實標註）。
                      dataEffect={`update:${pageId}`}
                      onClick={() => {
                        void sendVerify(row, 'NOT');
                      }}
                    >
                      送出審核
                    </Button>
                  ) : null}
                  {canPass ? (
                    <>
                      <Button
                        uiId={`${pageId}-result-row-${id}-verify-pass`}
                        variant="primary"
                        size="sm"
                        type="button"
                        disabled={busy}
                        dataEffect={`update:${pageId}`}
                        onClick={() => {
                          void sendVerify(row, 'PASS');
                        }}
                      >
                        通過
                      </Button>{' '}
                      <Button
                        uiId={`${pageId}-result-row-${id}-verify-fail`}
                        variant="danger"
                        size="sm"
                        type="button"
                        disabled={busy}
                        dataEffect={`update:${pageId}`}
                        onClick={() => {
                          void sendVerify(row, 'FAIL');
                        }}
                      >
                        未通過
                      </Button>
                    </>
                  ) : null}
                  {hasNew ? <><br /><span className="text-danger">有新版待審查</span></> : null}
                </span>
              );
            }
            return String(row[columnKey] ?? '');
          }}
        />
      </ContentPanel>
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 共用表單設定（form-template-common-form-template；crud-list 頁型 → complexBody 置換）
// ---------------------------------------------------------------------------

/**
 * 共用表單設定（commonFormTemplate.jsp:15-353）：查詢 3 欄＋表 6 欄（刪除鈕/表單名稱
 * formTemplateName/表單用途 plan→訓練計畫、class→課程、else N/A/來源 3 欄 srcOrgShortName/
 * srcJobTypeName/srcDeptName，:21-50）＋doQuery（:100-119 getCommonFormTemplateListAjax、
 * order col1 asc）＋doDelete（:121-138 sysConfirm『是否確定要刪除?』→
 * deleteCommonFormTemplateAjax→doQuery＋showInfoDialog(desc)）＋新增鈕→showAddModal
 * （:178-180）→CommonFormTemplateAddModal（本頁泛型表頭（醫院/訓練類別/部門）與 legacy 表
 * 不符（raw-dom 捕捉到查詢表 select 標籤），故整頁置換）。
 */
export function CommonFormTemplateBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [addOpen, setAddOpen] = useState(false);
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);
  // 查詢表單值（lift 到 body：doQuery 的 filters 要帶查詢欄＝C5-h；legacy 查詢欄全選填）。
  const [queryValues, setQueryValues] = useState<Record<string, string>>({
    organizationId: ACCOUNT_ORGANIZATION_OPTIONS[0]!.value,
    jobTypeId: '',
    purpose: '',
  });
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);

  async function doQuery(): Promise<void> {
    setBusy(true);
    try {
      // formTemplate/getCommonFormTemplateListAjax（:107）；filters 帶查詢欄（C5-h；
      // server 端過濾語意＝種子直出 D23-3）。
      const result = await dataSource.list(pageId, { page: 1, pageSize: 50, filters: { ...queryValues } });
      setRows(result.rows);
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 刪除（doDelete :121-138）。 */
  async function doDelete(row: Row): Promise<void> {
    const confirmed = await dialogs.confirm('是否確定要刪除?');
    if (!confirmed) {
      return;
    }
    setBusy(true);
    try {
      // formTemplate/deleteCommonFormTemplateAjax（:125；C5-j：真呼叫 DataSource）。
      await dataSource.remove(pageId, String(row['id'] ?? ''));
      await doQuery();
      await dialogs.info('刪除成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 表單用途映射（:32-40：plan→訓練計畫/class→課程/else N/A）。 */
  function purposeDisplayOf(row: Row): string {
    switch (String(row['purpose'] ?? '')) {
      case 'plan':
        return '訓練計畫';
      case 'class':
        return '課程';
      default:
        return 'N/A';
    }
  }

  // 批次 43（#41）：複雜表頭（commonFormTemplate.jsp:249-259）——headerColumns＝第一列
  // （rowspan=2/colspan=3 合併）＋secondHeaderRow＝第二列（spring:message 逐字：醫院/職類/部門）；
  // columns（資料欄）不變——tbody 照 6 欄全量渲染。
  const columns: readonly TableColumn[] = [
    { key: 'delete', header: '', align: 'center' },
    { key: 'formTemplateName', header: '表單名稱', align: 'center' },
    { key: 'purpose', header: '表單用途', align: 'center' },
    { key: 'srcOrgShortName', header: '來源組織', align: 'center' },
    { key: 'srcJobTypeName', header: '來源職類', align: 'center' },
    { key: 'srcDeptName', header: '來源部門', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      {/* 新增共用表單 modal（本頁唯一契約 modal；自訂互動版取代泛型 modal——三段驗證鏉＋
          級聯池平列，commonFormTemplate.jsp:269-353；新增鈕不帶 dataModalTrigger）。 */}
      {addOpen ? (
        <CommonFormTemplateAddModal
          pageId={pageId}
          onClose={() => {
            setAddOpen(false);
          }}
          onClosed={() => {
            void doQuery();
          }}
        />
      ) : null}
      <ContentPanel uiId={`${pageId}-panel`} title="共用表單設定" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢表單（commonFormTemplate.jsp:186-236；3 欄無 required——legacy 查詢欄全部選填）。
            批次 43（#41）：包 legacy 的 form（form[2] method="post"；R-diff form[2] 14 節點對位）。 */}
        <form data-ui-id={`${pageId}-query-form`} method="post" onSubmit={(event) => event.preventDefault()}>
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          {(
            [
              // 批次 43：label 逐字（* 醫院：/* 訓練類別：，commonFormTemplate.jsp 星號）＋
              // required 鏡像（:191/:201 required）＋purpose 首項=全部（去 placeholder）。
              ['organizationId', '* 醫院：', overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS, true],
              ['jobTypeId', '* 訓練類別：', overrideOptions('jobTypeId') ?? ACCOUNT_JOB_TYPE_OPTIONS, true],
              ['purpose', '用途：', [{ value: '', label: '全部' }, { value: 'plan', label: '訓練計畫' }, { value: 'class', label: '課程' }], false],
            ] as const
          ).map(([keyName, label, options, required]) => (
            <div key={keyName} className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-query-${keyName}`}>
                {label}
              </label>
              <Select
                uiId={`${pageId}-query-${keyName}`}
                name={keyName}
                value={queryValues[keyName] ?? ''}
                onChange={(next) => setQueryValues((current) => ({ ...current, [keyName]: next }))}
                options={options}
                {...(required ? { required: true } : {})}
              />
            </div>
          ))}
          <div className="col-span-2 flex gap-2">
            {/* 新增（:180 showAddModal → #dialogModal）。 */}
            <Button uiId={`${pageId}-add-btn`} variant="primary" size="sm" type="button" onClick={() => setAddOpen(true)}>
              新增
            </Button>
            <Button uiId={`${pageId}-query-btn`} variant="default" size="sm" type="button" disabled={busy} onClick={() => void doQuery()}>
              查詢
            </Button>
          </div>
        </div>
        </form>
        {/* 結果表（欄定義 commonFormTemplate.jsp:21-50；6 欄）＋批次 43 複雜表頭
            （headerColumns 第一列合併＋secondHeaderRow 第二列；columns＝tbody 6 欄不變）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          headerColumns={[
            { key: 'delete', header: '', align: 'center' as const, rowSpan: 2 },
            { key: 'formTemplateName', header: '表單名稱', align: 'center' as const, rowSpan: 2 },
            { key: 'purpose', header: '表單用途', align: 'center' as const, rowSpan: 2 },
            { key: 'srcOrgShortName', header: '表單來源', align: 'center' as const, colSpan: 3 },
          ]}
          secondHeaderRow={[
            { header: '醫院', align: 'center' as const },
            { header: '訓練類別', align: 'center' as const },
            { header: '部門', align: 'center' as const },
          ]}
          rows={rows}
          rowKey={(row) => String(row['id'] ?? '').toLowerCase()}
          emptyText={t('table.empty')}
          renderCell={(row, columnKey) => {
            const id = String(row['id'] ?? '').toLowerCase();
            if (columnKey === 'delete') {
              // 刪除鈕（:25-33；isPermitDelete 權限條件簡化＝D23-5 同款）。
              return (
                <Button
                  uiId={`${pageId}-result-row-${id}-delete`}
                  variant="danger"
                  size="sm"
                  type="button"
                  disabled={busy}
                  dataEffect={`remove:${pageId}`}
                  onClick={() => {
                    void doDelete(row);
                  }}
                >
                  刪除
                </Button>
              );
            }
            if (columnKey === 'purpose') {
              return purposeDisplayOf(row);
            }
            return String(row[columnKey] ?? '');
          }}
        />
      </ContentPanel>
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}
